import { config } from '../config';
import SessionService from './SessionService';
import appCache from './AppCache';

const API_URL = config.API_BASE_URL;

export interface SubscriptionData {
  id: number;
  status: string;
  planName?: string;
  planId: number;
  recurrence?: string;
  amountCents?: number;
  currency?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  firstChargeDate?: string;
}

export interface PaymentData {
  id: number;
  amountCents: number;
  currency: string;
  status: string;
  paidAt?: string;
  invoicePdfUrl?: string;
  createdAt: string;
}

export interface PlanData {
  id: number;
  name: string;
  recurrence: string;
  amountCents: number;
  currency: string;
}

export const getSubscription = async (forceRefresh = false): Promise<{ subscription: SubscriptionData | null }> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Not authenticated');

  if (!forceRefresh) {
    const cached = appCache.get<{ subscription: SubscriptionData | null }>('subscription');
    if (cached) return cached;
  }

  const res = await fetch(`${API_URL}/subscription`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch subscription');
  const data = await res.json();
  appCache.set('subscription', data, 5 * 60 * 1000);
  return data;
};

export const getPlans = async (): Promise<{ plans: PlanData[] }> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_URL}/subscription/plans`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch plans');
  return res.json();
};

export const createCheckout = async (planId: number): Promise<{ url: string }> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_URL}/subscription/checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ planId }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to create checkout');
  }
  return res.json();
};

export const createPortalSession = async (): Promise<{ url: string }> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_URL}/subscription/portal`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to open portal');
  }
  return res.json();
};

export const getPayments = async (): Promise<{ payments: PaymentData[] }> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_URL}/subscription/payments`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch payments');
  return res.json();
};

export const getInvoices = async (): Promise<{ invoices: PaymentData[] }> => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_URL}/subscription/invoices`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch invoices');
  return res.json();
};
